import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { RootState } from '@/store';
import { API_ENDPOINTS, API_SLICE, BASE_URL } from '@/utils/constants/apiEndpoints.ts';

import { Organization } from '@/types/organization';
import { GetPractitionersQuery, GetPractitionersResponse } from '@/types/user';
import {
  AnalyticsPagesPreferences,
  PatientListPagePreferences,
  TaskPagePreferences
} from '@/types/user-preferences';

export interface InternalUser {
  id: string;
  given: string;
  family: string;
  email?: string;
}

export interface ListInternalUsersResponse {
  data: InternalUser[];
}

export interface UserSettings {
  filterId: string;
  clinics: Organization[];
  filteredClinics: Organization[];
  practitioners: Organization[];
  filteredPractitioners: Organization[];
  filteredSigningPractitioners: Organization[];
  deviceTypes: string[];
  vendors: string[];
  patientStatuses: string[];
  includeUnassignedPractitioner: boolean;
  includeUnassignedSigningPractitioner: boolean;
  patientListPagePreferences: PatientListPagePreferences | null;
  analyticsPagePreferences: AnalyticsPagesPreferences | null;
  taskPagePreferences?: TaskPagePreferences | null;
}

export const userApiSlice = createApi({
  reducerPath: 'userApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  tagTypes: [API_SLICE.User],
  endpoints: (builder) => ({
    getUserSettings: builder.query<UserSettings, void>({
      query: () => ({
        url: API_ENDPOINTS.USERS.USER_SETTINGS,
        method: 'GET'
      }),
      providesTags: [{ type: API_SLICE.User, id: 'CURRENT_USER' }]
    }),

    updateUserSettings: builder.mutation<
      UserSettings,
      {
        settingsId: string;
        organizationIds?: string[];
        practitionerIds?: string[];
        signingPractitionerIds?: string[];
        deviceTypes?: string[];
        vendors?: string[];
        includeUnassignedPractitioner?: boolean;
        includeUnassignedSigningPractitioner?: boolean;
        patientListPagePreferences?: PatientListPagePreferences;
        analyticsPagePreferences?: AnalyticsPagesPreferences;
      }
    >({
      query: ({ settingsId, ...body }) => ({
        url: API_ENDPOINTS.USERS.UPDATE_GLOBAL_SETTINGS(settingsId),
        method: 'PATCH',
        body
      }),
      invalidatesTags: (_result, _error, _args) => [{ type: API_SLICE.User, id: 'CURRENT_USER' }]
    }),

    updatePagePreferences: builder.mutation<
      UserSettings,
      {
        settingsId: string;
        patientListPagePreferences?: PatientListPagePreferences;
        analyticsPagePreferences?: AnalyticsPagesPreferences;
        taskPagePreferences?: TaskPagePreferences;
      }
    >({
      query: ({ settingsId, ...body }) => ({
        url: API_ENDPOINTS.USERS.UPDATE_PAGE_PREFERENCES(settingsId),
        method: 'PATCH',
        body
      }),
      invalidatesTags: (_result, _error, _args) => [{ type: API_SLICE.User, id: 'CURRENT_USER' }]
    }),

    getPractitionersByClinicId: builder.query<GetPractitionersResponse, GetPractitionersQuery>({
      query: ({ clinicIds }) => ({
        url: API_ENDPOINTS.USERS.PRACTITIONERS_BY_CLINIC_ID,
        method: 'POST',
        body: { clinicIds, fetchAll: false }
      })
    }),
    listInternalUsers: builder.query<ListInternalUsersResponse, { search?: string }>({
      query: ({ search } = {}) => ({
        url: '/users/paginated',
        method: 'POST',
        body: search ? { search } : {}
      })
    }),
    getUserById: builder.query<{ user: { id: string; given?: string; family?: string } }, string>({
      query: (userId) => ({
        url: `/users/${userId}`,
        method: 'GET'
      })
    })
  })
});

export const {
  useGetUserSettingsQuery,
  useUpdateUserSettingsMutation,
  useUpdatePagePreferencesMutation,
  useGetPractitionersByClinicIdQuery,
  useLazyListInternalUsersQuery,
  useGetUserByIdQuery
} = userApiSlice
